import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import LoadingSpinner from "component/LoadingSpinner";
import CustomDropdown from "component/CustomDropdown";
import FormService from "service/FormService";
import { FormSaveReq } from "domain/payload/request/form/FormSaveReq";
import { Option } from "domain/model/Option";
import { FormFieldReq } from "domain/payload/request/form/FormFieldReq";
import { FieldOptionInfo, SectionInfo } from "components/form/ActionRulesModal";
import { ActionRulesInlineBuilder } from "components/form/ActionRulesInlineBuilder";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface FormSection {
  id?: string;
  name: string;
  description: string;
  actionRules: string;
  color?: string;
  isEditing?: boolean;
  sort?: number;
  fields: FormFieldReq[];
}

interface FormValidation {
  id?: string;
  validationName: string;
  ruleType: string;
  rulesValue: string;
}

const AttachmentUpload = ({
  sectionIndex,
  fieldIndex,
  field,
  onAttachmentSelect,
  onAttachmentDelete,
}: {
  sectionIndex: number;
  fieldIndex: number;
  field: FormFieldReq;
  onAttachmentSelect: (
    sectionIndex: number,
    fieldIndex: number,
    file: File | null
  ) => void;
  onAttachmentDelete: (
    sectionIndex: number,
    fieldIndex: number
  ) => void;
}) => {
  const hasAttachment = !!field.attachmentUrl;
  const attachmentUrl = field.attachmentUrl || undefined;

  return (
    <div className='mb-3'>
      <label className="form-label fw-semibold">Attachment Image</label>
      <div className='d-flex gap-3'>
        {hasAttachment ? (
          <div style={{ position: "relative", width: "120px", height: "120px" }}>
            <img
              src={attachmentUrl}
              className='rounded border'
              alt="Attachment Preview"
              style={{
                width: '120px',
                height: '120px',
                objectFit: 'contain'
              }}
            />

            <button
              type="button"
              className="btn btn-small-compact btn-danger"
              style={{
                position: "absolute",
                top: 4,
                right: 4,
                lineHeight: 1,
              }}
              onClick={() => onAttachmentDelete(sectionIndex, fieldIndex)}
              title="Delete Image"
            >
              <i className="fa-solid fa-trash"></i>
            </button>
          </div>
        ) : (
          <span
            className='rounded col-auto border text-center d-flex align-items-center justify-content-center'
            style={{ width: '120px', height: '120px' }}
          >
            No Image
          </span>
        )}

        <div className='col'>
          <input
            type="file"
            className="form-control form-control-compact"
            accept="image/jpeg,image/png"
            onChange={(e) =>
              onAttachmentSelect(
                sectionIndex,
                fieldIndex,
                e.target.files?.[0] || null
              )
            }
          />
          <small className="text-muted d-block mt-1">
            JPG/PNG only, max 500KB
          </small>
        </div>
      </div>
    </div>
  );
};

AttachmentUpload.displayName = 'AttachmentUpload';

const DISPLAY_TYPE_OPTIONS = [
  { id: "full", name: "Full" },
  { id: "simple", name: "Simple" },
];

interface CameraRatioOption {
  id: string;
  legacyId?: string;
  label: string;
  ratio: string;
  icon: React.ReactNode;
}

const CAMERA_RATIO_OPTIONS: CameraRatioOption[] = [
  {
    id: "portrait916",
    legacyId: "portrait",
    label: "Portrait",
    ratio: "9:16",
    icon: (
      <svg
        width="8"
        height="12"
        viewBox="0 0 8 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="flex-shrink-0"
        style={{ display: "inline-block", verticalAlign: "middle" }}
      >
        <rect
          x="0.6"
          y="0.6"
          width="6.8"
          height="10.8"
          rx="1"
          stroke="currentColor"
          strokeWidth="1.2"
        />
      </svg>
    ),
  },
  {
    id: "portrait34",
    label: "Portrait",
    ratio: "3:4",
    icon: (
      <svg
        width="9"
        height="12"
        viewBox="0 0 9 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="flex-shrink-0"
        style={{ display: "inline-block", verticalAlign: "middle" }}
      >
        <rect
          x="0.6"
          y="0.6"
          width="7.8"
          height="10.8"
          rx="1"
          stroke="currentColor"
          strokeWidth="1.2"
        />
      </svg>
    ),
  },
  {
    id: "square",
    label: "Square",
    ratio: "1:1",
    icon: (
      <svg
        width="10"
        height="10"
        viewBox="0 0 10 10"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="flex-shrink-0"
        style={{ display: "inline-block", verticalAlign: "middle" }}
      >
        <rect
          x="0.6"
          y="0.6"
          width="8.8"
          height="8.8"
          rx="1"
          stroke="currentColor"
          strokeWidth="1.2"
        />
      </svg>
    ),
  },
  {
    id: "landscape43",
    label: "Landscape",
    ratio: "4:3",
    icon: (
      <svg
        width="12"
        height="9"
        viewBox="0 0 12 9"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="flex-shrink-0"
        style={{ display: "inline-block", verticalAlign: "middle" }}
      >
        <rect
          x="0.6"
          y="0.6"
          width="10.8"
          height="7.8"
          rx="1"
          stroke="currentColor"
          strokeWidth="1.2"
        />
      </svg>
    ),
  },
  {
    id: "landscape169",
    legacyId: "landscape",
    label: "Landscape",
    ratio: "16:9",
    icon: (
      <svg
        width="12"
        height="8"
        viewBox="0 0 12 8"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="flex-shrink-0"
        style={{ display: "inline-block", verticalAlign: "middle" }}
      >
        <rect
          x="0.6"
          y="0.6"
          width="10.8"
          height="6.8"
          rx="1"
          stroke="currentColor"
          strokeWidth="1.2"
        />
      </svg>
    ),
  },
];


const FormFactory: React.FC = () => {
  const { id } = useParams();
  const { profile } = useSelector((state: any) => state.auth);
  const navigate = useNavigate();
  const formService = FormService();

  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formVersion, setFormVersion] = useState<number>(1);
  const [sections, setSections] = useState<FormSection[]>([]);
  const [typeList, setTypeList] = useState<Option[]>([]);
  const [isLoadingSave, setIsLoadingSave] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [selectedFieldForOptions, setSelectedFieldForOptions] = useState<{
    sectionIndex: number;
    fieldIndex: number;
  } | null>(null);

  const [globalRuleCodeMode, setGlobalRuleCodeMode] = useState<boolean>(false);
  const [activeSectionIdx, setActiveSectionIdx] = useState<number>(0);
  const [openSectionsInModal, setOpenSectionsInModal] = useState<Record<number, boolean>>({});

  // --- Dirty-state tracking for Save button (Enhancement #4) ---
  const [isDirty, setIsDirty] = useState(false);
  const isHydrating = useRef(true); // true while initial fetchDetail/fetchConfig is in progress
  const suppressDirtyRef = useRef(false); // true when the next setSections/state change is an internal sync, not a user edit

  useEffect(() => {
    if (isHydrating.current) return;
    if (suppressDirtyRef.current) {
      suppressDirtyRef.current = false;
      return;
    }
    setIsDirty(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formName, formDescription, sections]);

  const toggleSectionInModal = (index: number) => {
    setOpenSectionsInModal((prev) => {
      const current = prev[index] !== undefined ? prev[index] : false;
      return { ...prev, [index]: !current };
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      sections.forEach((_, idx) => {
        const el = document.getElementById(`section-card-${idx}`);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 200 && rect.bottom >= 100) {
            setActiveSectionIdx(idx);
          }
        }
      });
    };

    window.addEventListener("scroll", handleScroll, true);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [sections.length]);

  const allFieldsForRules: FieldOptionInfo[] = sections.flatMap((sec) =>
    sec.fields.map((f) => ({
      id: f.id,
      label: f.label,
      options: f.option,
      sectionName: sec.name,
    }))
  );

  const allSectionsForRules: SectionInfo[] = sections.map((sec) => ({
    id: sec.id,
    name: sec.name,
  }));

  // --- Enhancement #1: generate id in FE immediately with uuid ---
  const handleAddSection = () => {
    const newSection: FormSection = {
      id: uuidv4(),
      name: `Section ${sections.length + 1}`,
      description: "",
      actionRules: "",
      color: "",
      isEditing: false,
      fields: [],
    };
    if (!isHydrating.current) setIsDirty(true);
    setSections([...sections, newSection]);
  };

  const handleRemoveSection = (index: number) => {
    if (!window.confirm(`Are you sure you want to delete section "${sections[index].name}"?`)) return;
    const updated = [...sections];
    updated.splice(index, 1);
    if (!isHydrating.current) setIsDirty(true);
    setSections(updated);
  };

  const handleToggleEditSection = (index: number) => {
    const updated = [...sections];
    updated[index].isEditing = !updated[index].isEditing;
    setSections(updated);
  };

  const handleSectionChange = (index: number, key: string, value: any) => {
    const updated = sections.map((sec, idx) => {
      if (idx !== index) return sec;
      return { ...sec, [key]: value };
    });
    if (!isHydrating.current) setIsDirty(true);
    setSections(updated);
  };

  // --- Enhancement #1: generate id in FE immediately with uuid ---
  const handleAddField = (sectionIndex: number) => {
    const updated = sections.map((sec, sIdx) => {
      if (sIdx !== sectionIndex) return sec;
      return {
        ...sec,
        fields: [
          ...sec.fields,
          {
            id: uuidv4(),
            label: "",
            typeKey: 1,
            hint: "",
            isQcField: false,
            option: [],
            min: null,
            max: null,
            actionRules: "",
            amount: null,
            methodCamera: false,
            methodGallery: false,
            enableCameraMethod: "",
            availableRatioCameraInApps: "",
            isTitleKey: false,
            isThumbnail: false,
            attachmentUrl: "",
            _pendingImageFile: null,
            displayType: "full",
          },
        ],
      };
    });
    if (!isHydrating.current) setIsDirty(true);
    setSections(updated);
  };

  const handleRemoveField = (sectionIndex: number, fieldIndex: number) => {
    if (!window.confirm(`Are you sure you want to delete field "${sections[sectionIndex].fields[fieldIndex].label}"?`)) return;
    const updated = sections.map((sec, sIdx) => {
      if (sIdx !== sectionIndex) return sec;
      return {
        ...sec,
        fields: sec.fields.filter((_, fIdx) => fIdx !== fieldIndex),
      };
    });
    if (!isHydrating.current) setIsDirty(true);
    setSections(updated);
  };

  const handleFieldChange = (
    sectionIndex: number,
    fieldIndex: number,
    key: string,
    value: any
  ) => {
    const updated = sections.map((sec, sIdx) => {
      if (sIdx !== sectionIndex) return sec;
      return {
        ...sec,
        fields: sec.fields.map((f, fIdx) => {
          if (fIdx !== fieldIndex) return f;
          return {
            ...f,
            [key]: value,
            ...(key === "typeKey" && value !== 3 ? { isThumbnail: false } : {}),
            ...(key === "typeKey" && (Number(value) === 5 || Number(value) === 6) && !f.displayType ? { displayType: "full" } : {}),
          };
        }),
      };
    });
    if (!isHydrating.current) setIsDirty(true);
    setSections(updated);
  };

  const handleTitleKeyChange = (sectionIndex: number, fieldIndex: number) => {
    const updated = sections.map((sec, sIdx) => {
      if (sIdx !== sectionIndex) return sec;
      const currentVal = sec.fields[fieldIndex]?.isTitleKey || false;
      return {
        ...sec,
        fields: sec.fields.map((f, fIdx) => ({
          ...f,
          isTitleKey: fIdx === fieldIndex ? !currentVal : false,
        })),
      };
    });
    if (!isHydrating.current) setIsDirty(true);
    setSections(updated);
  };

  const handleThumbnailChange = (sectionIndex: number, fieldIndex: number) => {
    const updated = sections.map((sec, sIdx) => {
      if (sIdx !== sectionIndex) return sec;
      const currentVal = sec.fields[fieldIndex]?.isThumbnail || false;
      return {
        ...sec,
        fields: sec.fields.map((f, fIdx) => ({
          ...f,
          isThumbnail: fIdx === fieldIndex ? !currentVal : false,
        })),
      };
    });
    if (!isHydrating.current) setIsDirty(true);
    setSections(updated);
  };

  const handleAddOption = (sectionIndex: number, fieldIndex: number) => {
    const updated = sections.map((sec, sIdx) => {
      if (sIdx !== sectionIndex) return sec;
      return {
        ...sec,
        fields: sec.fields.map((f, fIdx) => {
          if (fIdx !== fieldIndex) return f;
          return { ...f, option: [...(f.option || []), ""] };
        }),
      };
    });
    if (!isHydrating.current) setIsDirty(true);
    setSections(updated);
  };

  const handleOptionChange = (
    sectionIndex: number,
    fieldIndex: number,
    optionIndex: number,
    value: string
  ) => {
    const updated = sections.map((sec, sIdx) => {
      if (sIdx !== sectionIndex) return sec;
      return {
        ...sec,
        fields: sec.fields.map((f, fIdx) => {
          if (fIdx !== fieldIndex) return f;
          const newOpts = [...(f.option || [])];
          newOpts[optionIndex] = value;
          return { ...f, option: newOpts };
        }),
      };
    });
    if (!isHydrating.current) setIsDirty(true);
    setSections(updated);
  };

  const handleRemoveOption = (
    sectionIndex: number,
    fieldIndex: number,
    optionIndex: number
  ) => {
    const updated = sections.map((sec, sIdx) => {
      if (sIdx !== sectionIndex) return sec;
      return {
        ...sec,
        fields: sec.fields.map((f, fIdx) => {
          if (fIdx !== fieldIndex) return f;
          const newOpts = [...(f.option || [])];
          newOpts.splice(optionIndex, 1);
          return { ...f, option: newOpts };
        }),
      };
    });
    if (!isHydrating.current) setIsDirty(true);
    setSections(updated);
  };

  const handleDragOptionEndInModal = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !selectedFieldForOptions) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    if (activeId.startsWith("modal-option-") && overId.startsWith("modal-option-")) {
      const activeIndex = parseInt(activeId.split("-")[2]);
      const overIndex = parseInt(overId.split("-")[2]);

      setSections((prev) => {
        const updated = [...prev];
        const field = updated[selectedFieldForOptions.sectionIndex].fields[selectedFieldForOptions.fieldIndex];
        if (field.option) {
          field.option = arrayMove(field.option, activeIndex, overIndex);
        }
        return updated;
      });
    }
  };

  const MAX_SIZE = 500 * 1024;

  const handleAttachmentFileSelect = useCallback((
    sectionIndex: number,
    fieldIndex: number,
    file: File | null
  ) => {
    if (!file) return;

    if (file.type !== "image/jpeg" && file.type !== "image/png") {
      alert("Format gambar harus JPG atau PNG");
      return;
    }

    if (file.size > MAX_SIZE) {
      alert("Ukuran file maksimal 500 KB");
      return;
    }

    setSections(prev => {
      const updated = [...prev];
      const field = updated[sectionIndex].fields[fieldIndex];
      field._pendingImageFile = file;
      const previewUrl = URL.createObjectURL(file);
      field.attachmentUrl = previewUrl;
      return updated;
    });
  }, []);

  const uploadImage = async (fieldId: string, file: File): Promise<string | null> => {
    try {
      const response = await formService.formFieldUploadImage(
        profile.data.token,
        fieldId,
        file
      );
      if (response.status === 'T' && response.data) {
        return response.data.attachmentUrl;
      }
      return null;
    } catch (error) {
      console.error(`Upload failed for field ${fieldId}:`, error);
      return null;
    }
  };

  const uploadAllPendingImages = async (
    currentSections: FormSection[]
  ): Promise<FormSection[]> => {
    const updatedSections = [...currentSections];
    const uploadPromises: Promise<void>[] = [];

    for (let sIdx = 0; sIdx < updatedSections.length; sIdx++) {
      const section = updatedSections[sIdx];
      for (let fIdx = 0; fIdx < section.fields.length; fIdx++) {
        const field = section.fields[fIdx];
        const pendingFile = field._pendingImageFile;

        if (pendingFile && field.id) {
          const promise = (async () => {
            const uploadedUrl = await uploadImage(field.id!, pendingFile);
            if (uploadedUrl) {
              updatedSections[sIdx].fields[fIdx].attachmentUrl = uploadedUrl;
              updatedSections[sIdx].fields[fIdx]._pendingImageFile = undefined;
              if (field.attachmentUrl?.startsWith('blob:')) {
                URL.revokeObjectURL(field.attachmentUrl);
              }
            }
          })();
          uploadPromises.push(promise);
        }
      }
    }

    await Promise.all(uploadPromises);
    return updatedSections;
  };

  const handleAttachmentDelete = useCallback(
    (sectionIndex: number, fieldIndex: number) => {
      setSections(prev => {
        const updated = [...prev];

        const field = updated[sectionIndex].fields[fieldIndex];

        if (field.attachmentUrl?.startsWith("blob:")) {
          URL.revokeObjectURL(field.attachmentUrl);
        }

        field.attachmentUrl = "";
        field._pendingImageFile = null;

        return updated;
      });
    },
    []
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoadingSave(true);

    try {
      const req = new FormSaveReq();
      req.id = id !== "0" ? id : undefined;
      req.name = formName;
      req.description = formDescription;
      req.pages = sections.map((s, sIdx) => ({
        id: s.id,
        name: s.name,
        description: s.description,
        actionRules: s.actionRules,
        color: s.color || "",
        sort: sIdx + 1,
        fields: s.fields.map((f, fIdx) => {
          const isSelection = Number(f.typeKey) === 5 || Number(f.typeKey) === 6;
          const displayTypeValue = isSelection ? (f.displayType || "full") : undefined;
          return {
            id: f.id || undefined,
            typeKey: f.typeKey,
            label: f.label,
            min: f.min ?? null,
            max: f.max ?? null,
            hint: f.hint,
            actionRules: f.actionRules || "",
            isQcField: f.isQcField,
            option: f.option,
            amount: f.amount ?? null,
            methodCamera: f.methodCamera || false,
            methodGallery: f.methodGallery || false,
            enableCameraMethod: f.enableCameraMethod || "",
            availableRatioCameraInApps: f.availableRatioCameraInApps || "",
            sort: fIdx + 1,
            isTitleKey: f.isTitleKey || false,
            isThumbnail: f.isThumbnail || false,
            attachmentUrl: f.attachmentUrl?.startsWith('blob:') ? "" : f.attachmentUrl || "",
            displayType: displayTypeValue,
            displayTyle: displayTypeValue,
            display_type: displayTypeValue,
          };
        }),
      }));

      req.validations = [];

      const response = await formService.formSave(profile.data.token, req);

      if (response.status !== 'T') {
        alert(response.message || "Failed to save form");
        return;
      }

      const savedData = response.data;
      let updatedSections = [...sections];

      if (savedData && savedData.pages && savedData.pages.length > 0) {
        updatedSections = sections.map((section, sIdx) => {
          const savedPage = savedData.pages?.[sIdx];
          if (!savedPage) return section;

          return {
            ...section,
            id: savedPage.id,
            fields: section.fields.map((field, fIdx) => {
              const savedField = savedPage.fields?.[fIdx];
              if (!savedField) return field;

              return {
                ...field,
                id: savedField.id,
              };
            }),
          };
        });

        // This sync is internal (not a user edit) - don't mark as dirty
        suppressDirtyRef.current = true;
        setSections(updatedSections);
      }

      const hasPendingImages = updatedSections.some(section =>
        section.fields.some(field => field._pendingImageFile)
      );

      if (hasPendingImages) {
        const finalSections = await uploadAllPendingImages(updatedSections);
        suppressDirtyRef.current = true;
        setSections(finalSections);
      }

      alert("Form saved successfully!");

      // Enhancement #4: form is now clean/saved
      setIsDirty(false);

      // Enhancement #5: stay on the form page instead of navigating back to the list.
      // If this was a brand-new form ("0"), move the URL to the new form's id so
      // subsequent actions (add section/field, re-save, refresh) stay consistent.
      if (id === "0" && savedData?.id) {
        navigate(`/form/${savedData.id}`, { replace: true });
      }

    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to save form';
      alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoadingSave(false);
    }
  };

  const fetchDetail = async () => {
    if (id === "0") return;
    setIsLoadingSave(true);
    try {
      const res = await formService.formDetail(profile.data.token, id ?? "0");

      setFormName(res.data?.name || "");
      setFormDescription(res.data?.description || "");
      setFormVersion(res.data?.version || 1);

      const pages = res.data?.pages || [];
      const mappedSections: FormSection[] = pages
        .sort((a: any, b: any) => a.sort - b.sort)
        .map((p: any) => ({
          id: p.id,
          name: p.name,
          description: p.description || "",
          actionRules: p.actionRules || "",
          color: p.color || "",
          sort: p.sort || 0,
          fields: (p.fields || [])
            .sort((a: any, b: any) => a.sort - b.sort)
            .map((f: any) => ({
              id: f.id,
              label: f.label,
              description: f.description,
              typeKey: f.type?.key || 1,
              min: f.min,
              max: f.max,
              hint: f.hint,
              actionRules: f.actionRules || "",
              isQcField: f.isQcField,
              option: f.options || [],
              amount: f.amount || null,
              methodCamera: f.methodCamera || false,
              methodGallery: f.methodGallery || false,
              enableCameraMethod: f.enableCameraMethod || "",
              availableRatioCameraInApps: f.availableRatioCameraInApps || "",
              sort: f.sort || 0,
              isTitleKey: f.isTitleKey || false,
              isThumbnail: f.isThumbnail || false,
              attachmentUrl: f.attachmentUrl || "",
              _pendingImageFile: null,
              displayType: f.displayType || f.display_type || f.displayTyle || ((f.type?.key === 5 || f.type?.key === 6) ? "full" : undefined),
            })),
        }));

      // Hydrating from server - this is not a user edit
      suppressDirtyRef.current = true;
      setSections(mappedSections);
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch form detail';
      alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoadingSave(false);
    }
  };

  const fetchConfig = async () => {
    try {
      const res = await formService.formConfig(profile.data.token);
      setTypeList(res.data?.fieldTypeList ?? []);
    } catch (err: any) {
      console.error(err);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor)
  );

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over) return;

    if (active.id !== over.id) {
      const oldIndex = sections.findIndex((s, idx) => `section-${idx}` === active.id);
      const newIndex = sections.findIndex((s, idx) => `section-${idx}` === over.id);

      setSections((prev) => arrayMove(prev, oldIndex, newIndex));
    }
  };

  const SortableSectionItem: React.FC<{
    id: string;
    section: FormSection;
    index: number;
    isOpen: boolean;
    onToggle: () => void;
    onDelete: (index: number) => void;
    children?: React.ReactNode;
  }> = ({ id, section, index, isOpen, onToggle, onDelete, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style: React.CSSProperties = {
      transform: CSS.Translate.toString(transform),
      transition: isDragging ? undefined : transition,
      opacity: isDragging ? 0.5 : 1,
      zIndex: isDragging ? 9999 : 'auto',
      position: 'relative',
    };

    return (
      <div ref={setNodeRef} style={style} className="accordion-item mb-3 border rounded">
        <h2 className="accordion-header d-flex align-items-center">
          <button
            className={`accordion-button fw-bold flex-grow-1 ${isOpen ? '' : 'collapsed'}`}
            type="button"
            onClick={onToggle}
          >
            <span
              className="drag-handle me-2"
              style={{ cursor: "grab" }}
              {...attributes}
              {...listeners}
              onClick={(e) => e.stopPropagation()}
            >
              ☰
            </span>
            {section.name}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger ms-2 me-2"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(index);
            }}
          >
            <i className="fa-solid fa-trash"></i>
          </button>
        </h2>

        <div className={`accordion-collapse collapse ${isOpen ? 'show' : ''}`}>
          <div className="accordion-body">{children}</div>
        </div>
      </div>
    );
  };

  const SortableFieldItem: React.FC<{
    id: string;
    sectionIndex: number;
    fieldIndex: number;
    field: FormFieldReq;
    onDelete: (sIdx: number, fIdx: number) => void;
  }> = ({ id, sectionIndex, fieldIndex, field, onDelete }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style: React.CSSProperties = {
      transform: CSS.Translate.toString(transform),
      transition: isDragging ? undefined : transition,
      opacity: isDragging ? 0.5 : 1,
      zIndex: isDragging ? 9999 : 'auto',
      position: 'relative',
    };

    return (
      <li
        ref={setNodeRef}
        style={style}
        className="list-group-item d-flex justify-content-between align-items-center"
      >
        <div className="d-flex align-items-center flex-grow-1 justify-content-between">
          <div className="d-flex align-items-center me-3" style={{ cursor: "grab" }} {...attributes} {...listeners}>
            <span className="drag-handle me-2">☰</span>
            <span>{field.label || `Field ${fieldIndex + 1}`}</span>
          </div>

          <div className="d-flex align-items-center gap-2">
            <select
              className="form-select form-select-sm"
              style={{ width: "150px" }}
              onChange={(e) => {
                if (!e.target.value) return;
                const targetSection = parseInt(e.target.value);
                if (window.confirm(`Move this field to Section ${targetSection + 1}?`)) {
                  handleMoveField(sectionIndex, fieldIndex, targetSection);
                }
              }}
              value=""
            >
              <option value="">Move to...</option>
              {sections.map((_, idx) => (
                <option key={idx} value={idx} disabled={idx === sectionIndex}>
                  Section {idx + 1}
                </option>
              ))}
            </select>

            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm("Are you sure you want to delete this field?")) {
                  onDelete(sectionIndex, fieldIndex);
                }
              }}
            >
              <i className="fa-solid fa-trash"></i>
            </button>
          </div>
        </div>
      </li>
    );
  };

  const SortableOptionItemModal: React.FC<{
    id: string;
    optionIndex: number;
    value: string;
    onRemove: (optIdx: number) => void;
    onChange: (optIdx: number, value: string) => void;
  }> = ({ id, optionIndex, value, onRemove, onChange }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style: React.CSSProperties = {
      transform: CSS.Translate.toString(transform),
      transition: isDragging ? undefined : transition,
      opacity: isDragging ? 0.5 : 1,
      zIndex: isDragging ? 9999 : 'auto',
      position: 'relative',
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        className="d-flex align-items-center mb-2"
      >
        <span
          className="drag-handle me-2"
          style={{ cursor: "grab", fontSize: "1.2rem" }}
          {...attributes}
          {...listeners}
        >
          ☰
        </span>
        <input
          type="text"
          className="form-control form-control-compact me-2 flex-grow-1"
          value={value}
          onChange={(e) => onChange(optionIndex, e.target.value)}
          placeholder={`Option ${optionIndex + 1}`}
        />
        <button
          type="button"
          className="btn btn-small-compact btn-danger"
          onClick={() => onRemove(optionIndex)}
        >
          <i className="fa-solid fa-trash"></i>
        </button>
      </div>
    );
  };

  const handleMoveField = (fromSectionIdx: number, fieldIdx: number, toSectionIdx: number) => {
    if (fromSectionIdx === toSectionIdx) return;

    setSections((prev) => {
      const updated = [...prev];
      const [movedField] = updated[fromSectionIdx].fields.splice(fieldIdx, 1);
      updated[toSectionIdx].fields.push(movedField);
      return updated;
    });
  };

  const handleAddOptionInModal = () => {
    if (!selectedFieldForOptions) return;
    const { sectionIndex, fieldIndex } = selectedFieldForOptions;
    handleAddOption(sectionIndex, fieldIndex);
  };

  const handleOptionChangeInModal = (optionIndex: number, value: string) => {
    if (!selectedFieldForOptions) return;
    const { sectionIndex, fieldIndex } = selectedFieldForOptions;
    handleOptionChange(sectionIndex, fieldIndex, optionIndex, value);
  };

  const handleRemoveOptionInModal = (optionIndex: number) => {
    if (!selectedFieldForOptions) return;
    const { sectionIndex, fieldIndex } = selectedFieldForOptions;
    handleRemoveOption(sectionIndex, fieldIndex, optionIndex);
  };



  useEffect(() => {
    const init = async () => {
      if (id !== "0") await fetchDetail();
      await fetchConfig();
      // Initial hydration finished - subsequent state changes should mark the form dirty
      isHydrating.current = false;
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedField = selectedFieldForOptions
    ? sections[selectedFieldForOptions.sectionIndex]?.fields[selectedFieldForOptions.fieldIndex]
    : null;

  return (
    <>
      <div className="pt-1 h-100" style={{ background: "#f2f4f8" }}>
        <div className="m-4">
          <h4 className="mb-4">{id === "0" ? "Add Form" : "Edit Form"}</h4>

          <div className="p-4 bg-white mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Form Information</h5>
              {id !== "0" && formVersion > 0 && (
                <span className="text-muted" style={{ fontSize: '16px' }}>
                  <i className="fa-regular fa-file me-1"></i>
                  v{formVersion}
                </span>
              )}
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold">Form Title</label>
              <input
                type="text"
                className="form-control form-control-compact"
                value={formName}
                onChange={(e) => {
                  setFormName(e.target.value);
                  if (!isHydrating.current) setIsDirty(true);
                }}
                required
              />
            </div>
            <div className="mb-3">
              <label className="form-label fw-semibold">Description</label>
              <textarea
                className="form-control form-control-compact"
                rows={3}
                value={formDescription}
                onChange={(e) => {
                  setFormDescription(e.target.value);
                  if (!isHydrating.current) setIsDirty(true);
                }}
              />
            </div>
          </div>

          {sections.map((section, sIdx) => (
            <div
              key={sIdx}
              id={`section-card-${sIdx}`}
              className="bg-white mb-4 border rounded shadow-sm position-relative"
            >
              {/* Sticky Header Bar for Section */}
              <div
                className="sticky-top bg-white border-bottom px-4 py-3 d-flex align-items-center justify-content-between shadow-sm"
                style={{
                  top: 0,
                  zIndex: 1020,
                  borderLeft: `5px solid ${section.color || '#3b82f6'}`,
                  borderTopLeftRadius: '0.375rem',
                  borderTopRightRadius: '0.375rem',
                }}
              >
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <span className="badge bg-primary px-2 py-1" style={{ fontSize: '0.75rem' }}>
                    Section #{sIdx + 1}
                  </span>

                  {section.isEditing ? (
                    <input
                      type="text"
                      className="form-control form-control-compact me-2"
                      style={{ width: '400px' }}
                      value={section.name}
                      onChange={(e) => handleSectionChange(sIdx, "name", e.target.value)}
                    />
                  ) : (
                    <h6 className="fw-bold me-2 mb-0 text-dark">{section.name}</h6>
                  )}

                  <button
                    type="button"
                    className="btn btn-small-compact btn-outline-secondary me-2"
                    onClick={() => handleToggleEditSection(sIdx)}
                    title={section.isEditing ? "Save Name" : "Edit Name"}
                  >
                    {section.isEditing ? <i className="fa-solid fa-check text-success"></i> : <i className="fa-solid fa-pen"></i>}
                  </button>

                  <div className="d-flex align-items-center gap-1 ms-2" style={{ fontStyle: 'italic', fontSize: '0.8rem', color: '#6c757d' }}>
                    <span>Section ID: {section.id || "New Section"}</span>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(section.id || "New Section");
                      }}
                      title="Copy ID"
                      className="btn btn-link btn-xs p-0 text-decoration-none text-muted ms-1"
                    >
                      <i className="fa-solid fa-copy"></i>
                    </button>
                  </div>
                </div>

                <div className="d-flex align-items-center gap-2">
                  <div className="d-flex align-items-center gap-1">
                    <label className="form-label fw-bold mb-0 small text-muted">Page Color:</label>
                    <input
                      type="color"
                      className="form-control form-control-color p-0 border-0 ms-1"
                      style={{
                        width: "35px",
                        height: "22px",
                        backgroundColor: section.color,
                        cursor: "pointer",
                        borderRadius: "4px",
                      }}
                      value={section.color}
                      onChange={(e) => handleSectionChange(sIdx, "color", e.target.value)}
                    />
                  </div>

                  <button
                    type="button"
                    className="btn btn-small-compact btn-danger ms-2"
                    onClick={() => handleRemoveSection(sIdx)}
                    title="Remove Section"
                  >
                    <i className="fa-solid fa-trash"></i>
                  </button>
                </div>
              </div>

              {/* Section Body */}
              <div className="p-4">

                <div className="row">
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label fw-bold">Description</label>
                      <textarea
                        className="form-control form-control-compact"
                        rows={3}
                        value={section.description}
                        onChange={(e) => handleSectionChange(sIdx, "description", e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md-8">
                    <div className="mb-3">
                      <label className="form-label fw-bold mb-1">Action Rules</label>
                      <ActionRulesInlineBuilder
                        value={section.actionRules || ""}
                        onChange={(newVal) => handleSectionChange(sIdx, "actionRules", newVal)}
                        allFields={allFieldsForRules}
                        allSections={allSectionsForRules}
                        forceRawMode={globalRuleCodeMode}
                      />
                    </div>
                  </div>
                </div>

                {section.fields.map((field, fIdx) => (
                  <div key={fIdx} className="border p-3 mb-3 rounded" style={{ backgroundColor: "#f9fafb" }}>
                    <div className="d-flex align-items-center" style={{ fontStyle: 'italic' }}>
                      <p className="mb-0">Field ID : {field.id || "New Field"}</p>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(field.id || "New Field")
                        }}
                        title="Copy ID"
                        className="btn btn-small-compact btn-outline-primary ms-2"
                      >
                        <i className="fa-solid fa-copy text-lg"></i>
                      </button>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="d-flex">
                        <label className="fw-semibold mb-0">Question</label>
                        <div className="form-check ms-3">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            name={`titleKey-section-${sIdx}`}
                            id={`titleKey-${sIdx}-${fIdx}`}
                            checked={field.isTitleKey || false}
                            onChange={() => handleTitleKeyChange(sIdx, fIdx)}
                          />
                          <label className="form-check-label" htmlFor={`titleKey-${sIdx}-${fIdx}`}>
                            Title Key
                          </label>
                        </div>
                        {field.typeKey === 3 && (
                          <div className="form-check ms-3">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              name={`thumbnail-section-${sIdx}`}
                              id={`thumbnail-${sIdx}-${fIdx}`}
                              checked={field.isThumbnail || false}
                              onChange={() => handleThumbnailChange(sIdx, fIdx)}
                            />
                            <label className="form-check-label" htmlFor={`thumbnail-${sIdx}-${fIdx}`}>
                              Thumbnail
                            </label>
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        className="btn btn-small-compact btn-danger"
                        onClick={() => handleRemoveField(sIdx, fIdx)}
                      >
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    </div>

                    <div className="row g-2">
                      <div className={(Number(field.typeKey) === 5 || Number(field.typeKey) === 6) ? "col-md-6" : "col-md-8"}>
                        <input
                          type="text"
                          className="form-control form-control-compact"
                          placeholder="Enter question"
                          value={field.label}
                          onChange={(e) => handleFieldChange(sIdx, fIdx, "label", e.target.value)}
                        />
                      </div>
                      <div className={(Number(field.typeKey) === 5 || Number(field.typeKey) === 6) ? "col-md-3" : "col-md-4"}>
                        <CustomDropdown
                          value={field.typeKey ?? null}
                          options={typeList.map((type) => ({
                            id: type.key,
                            name: type.value,
                          }))}
                          onChange={(opt) => handleFieldChange(sIdx, fIdx, "typeKey", Number(opt.id))}
                          containerClassName="w-100"
                          buttonClassName="form-control form-control-compact w-100 d-flex align-items-center justify-content-between text-start bg-white text-dark border rounded-3"
                        />
                      </div>
                      {(Number(field.typeKey) === 5 || Number(field.typeKey) === 6) && (
                        <div className="col-md-3">
                          <CustomDropdown
                            value={field.displayType || "full"}
                            options={DISPLAY_TYPE_OPTIONS}
                            onChange={(opt) => handleFieldChange(sIdx, fIdx, "displayType", String(opt.id))}
                            placeholder="Display Type"
                            containerClassName="w-100"
                            buttonClassName="form-control form-control-compact w-100 d-flex align-items-center justify-content-between text-start bg-white text-dark border rounded-3"
                          />
                        </div>
                      )}
                    </div>

                    {field.typeKey === 1 && (
                      <div className="row g-2 mt-2">
                        <div className="col-md-2">
                          <label className="fw-bold form-label">Min Character</label>
                          <input type="number" className="form-control form-control-compact" value={field.min ?? ""} onChange={(e) => handleFieldChange(sIdx, fIdx, "min", parseInt(e.target.value))} />
                        </div>
                        <div className="col-md-2">
                          <label className="fw-bold form-label">Max Character</label>
                          <input type="number" className="form-control form-control-compact" value={field.max ?? ""} onChange={(e) => handleFieldChange(sIdx, fIdx, "max", parseInt(e.target.value))} />
                        </div>
                        <div className="col-md-8">
                          <label className="fw-bold form-label mb-1">Action Rules</label>
                          <ActionRulesInlineBuilder
                            value={field.actionRules || ""}
                            onChange={(newVal) => handleFieldChange(sIdx, fIdx, "actionRules", newVal)}
                            allFields={allFieldsForRules}
                            allSections={allSectionsForRules}
                            forceRawMode={globalRuleCodeMode}
                            disablePreCondition={true}
                          />
                        </div>
                      </div>
                    )}

                    {field.typeKey === 2 && (
                      <div className="row g-2 mt-2">
                        <div className="col-md-2">
                          <label className="fw-bold form-label">Min Value</label>
                          <input type="number" className="form-control form-control-compact" value={field.min ?? ""} onChange={(e) => handleFieldChange(sIdx, fIdx, "min", parseInt(e.target.value))} />
                        </div>
                        <div className="col-md-2">
                          <label className="fw-bold form-label">Max Value</label>
                          <input type="number" className="form-control form-control-compact" value={field.max ?? ""} onChange={(e) => handleFieldChange(sIdx, fIdx, "max", parseInt(e.target.value))} />
                        </div>
                        <div className="col-md-8">
                          <label className="fw-bold form-label mb-1">Action Rules</label>
                          <ActionRulesInlineBuilder
                            value={field.actionRules || ""}
                            onChange={(newVal) => handleFieldChange(sIdx, fIdx, "actionRules", newVal)}
                            allFields={allFieldsForRules}
                            allSections={allSectionsForRules}
                            forceRawMode={globalRuleCodeMode}
                            disablePreCondition={true}
                          />
                        </div>
                      </div>
                    )}

                    {field.typeKey === 3 && (() => {
                      const selectedCameraModes = (field.enableCameraMethod || "")
                        .split(",")
                        .map((s: string) => s.trim().toLowerCase())
                        .filter(Boolean);
                      const selectedRatios = (field.availableRatioCameraInApps || "")
                        .split(",")
                        .map((s: string) => s.trim().toLowerCase())
                        .filter(Boolean);

                      const toggleCameraMode = (mode: string, checked: boolean) => {
                        let updated = [...selectedCameraModes];
                        const target = mode.toLowerCase();
                        if (checked) {
                          if (!updated.includes(target)) updated.push(target);
                        } else {
                          updated = updated.filter((m: string) => m.toLowerCase() !== target);
                        }
                        handleFieldChange(sIdx, fIdx, "enableCameraMethod", updated.join(","));
                      };

                      const toggleRatio = (opt: CameraRatioOption, checked: boolean) => {
                        let updated = [...selectedRatios];
                        const keys = opt.legacyId ? [opt.id, opt.legacyId] : [opt.id];
                        if (checked) {
                          keys.forEach((k) => {
                            if (!updated.includes(k)) updated.push(k);
                          });
                        } else {
                          updated = updated.filter((r: string) => !keys.includes(r.toLowerCase()));
                        }
                        handleFieldChange(sIdx, fIdx, "availableRatioCameraInApps", updated.join(","));
                      };

                      return (
                        <div className="mt-3">
                          <div className="row g-2 align-items-start">
                            <div className="col-md-6">
                              <label className="fw-bold form-label d-block mb-1">Method</label>
                              <div className="d-flex flex-wrap align-items-center" style={{ gap: "8px" }}>
                                <button
                                  type="button"
                                  onClick={() => handleFieldChange(sIdx, fIdx, "methodCamera", !field.methodCamera)}
                                  className={`btn btn-sm d-inline-flex align-items-center text-nowrap transition-all ${
                                    field.methodCamera ? "btn-primary shadow-sm" : "btn-outline-secondary"
                                  }`}
                                  style={{
                                    borderRadius: "5px",
                                    height: "26px",
                                    padding: "0 8px",
                                    fontSize: "0.75rem",
                                    gap: "5px",
                                  }}
                                >
                                  <i className="fa-solid fa-camera" style={{ fontSize: "11px" }} />
                                  <span className="fw-medium">Camera</span>
                                  {field.methodCamera && (
                                    <i
                                      className="fa-solid fa-check"
                                      style={{ fontSize: "8px" }}
                                    />
                                  )}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleFieldChange(sIdx, fIdx, "methodGallery", !field.methodGallery)}
                                  className={`btn btn-sm d-inline-flex align-items-center text-nowrap transition-all ${
                                    field.methodGallery ? "btn-primary shadow-sm" : "btn-outline-secondary"
                                  }`}
                                  style={{
                                    borderRadius: "5px",
                                    height: "26px",
                                    padding: "0 8px",
                                    fontSize: "0.75rem",
                                    gap: "5px",
                                  }}
                                >
                                  <i className="fa-solid fa-images" style={{ fontSize: "11px" }} />
                                  <span className="fw-medium">Gallery</span>
                                  {field.methodGallery && (
                                    <i
                                      className="fa-solid fa-check"
                                      style={{ fontSize: "8px" }}
                                    />
                                  )}
                                </button>
                              </div>

                              <div className="mt-2.5" style={{ marginTop: "10px" }}>
                                <label className="fw-bold form-label d-block mb-1">Camera Mode</label>
                                <div className="d-flex flex-wrap align-items-center" style={{ gap: "8px" }}>
                                  <button
                                    type="button"
                                    onClick={() => toggleCameraMode("in_apps", !selectedCameraModes.includes("in_apps"))}
                                    className={`btn btn-sm d-inline-flex align-items-center text-nowrap transition-all ${
                                      selectedCameraModes.includes("in_apps") ? "btn-primary shadow-sm" : "btn-outline-secondary"
                                    }`}
                                    style={{
                                      borderRadius: "5px",
                                      height: "26px",
                                      padding: "0 8px",
                                      fontSize: "0.75rem",
                                      gap: "5px",
                                    }}
                                  >
                                    <i className="fa-solid fa-mobile-screen" style={{ fontSize: "11px" }} />
                                    <span className="fw-medium">In-Apps</span>
                                    {selectedCameraModes.includes("in_apps") && (
                                      <i
                                        className="fa-solid fa-check"
                                        style={{ fontSize: "8px" }}
                                      />
                                    )}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => toggleCameraMode("external", !selectedCameraModes.includes("external"))}
                                    className={`btn btn-sm d-inline-flex align-items-center text-nowrap transition-all ${
                                      selectedCameraModes.includes("external") ? "btn-primary shadow-sm" : "btn-outline-secondary"
                                    }`}
                                    style={{
                                      borderRadius: "5px",
                                      height: "26px",
                                      padding: "0 8px",
                                      fontSize: "0.75rem",
                                      gap: "5px",
                                    }}
                                  >
                                    <i className="fa-solid fa-arrow-up-right-from-square" style={{ fontSize: "10px" }} />
                                    <span className="fw-medium">External</span>
                                    {selectedCameraModes.includes("external") && (
                                      <i
                                        className="fa-solid fa-check"
                                        style={{ fontSize: "8px" }}
                                      />
                                    )}
                                  </button>
                                </div>
                              </div>

                              <div className="mt-2.5" style={{ marginTop: "10px" }}>
                                <label className="fw-bold form-label d-block mb-1">Camera Ratio</label>
                                <div className="d-flex flex-wrap align-items-center" style={{ gap: "8px" }}>
                                  {CAMERA_RATIO_OPTIONS.map((opt) => {
                                    const isSelected = selectedRatios.includes(opt.id) || (opt.legacyId ? selectedRatios.includes(opt.legacyId) : false);
                                    return (
                                      <button
                                        key={opt.id}
                                        type="button"
                                        onClick={() => toggleRatio(opt, !isSelected)}
                                        className={`btn btn-sm d-inline-flex align-items-center text-nowrap transition-all ${
                                          isSelected
                                            ? "btn-primary shadow-sm"
                                            : "btn-outline-secondary"
                                        }`}
                                        style={{
                                          borderRadius: "5px",
                                          height: "26px",
                                          padding: "0 8px",
                                          fontSize: "0.75rem",
                                          gap: "5px",
                                        }}
                                      >
                                        {opt.icon}
                                        <span className="fw-medium">{opt.label}</span>
                                        <span
                                          className={isSelected ? "text-white-50" : "text-muted"}
                                          style={{ fontSize: "0.7rem" }}
                                        >
                                          ({opt.ratio})
                                        </span>
                                        {isSelected && (
                                          <i
                                            className="fa-solid fa-check"
                                            style={{ fontSize: "8px" }}
                                          />
                                        )}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                            <div className="col-md-6">
                              <label className="fw-bold form-label mb-1">Action Rules</label>
                              <ActionRulesInlineBuilder
                                value={field.actionRules || ""}
                                onChange={(newVal) => handleFieldChange(sIdx, fIdx, "actionRules", newVal)}
                                allFields={allFieldsForRules}
                                allSections={allSectionsForRules}
                                forceRawMode={globalRuleCodeMode}
                                disablePreCondition={true}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {(field.typeKey === 4 || field.typeKey === 5 || field.typeKey === 6) && (
                      <div className="row">
                        <div className="col-md-4">
                          {(field.typeKey === 5 || field.typeKey === 6) && (
                            <div className="pt-3">
                              {field.option && field.option.length > 0 && (
                                <>
                                  <label className="fw-semibold d-block mb-2">
                                    Options ({field.option.length})
                                  </label>
                                  <div className="mb-3">
                                    {field.option.map((opt, optIdx) => (
                                      <div key={optIdx} className="d-flex align-items-center mb-2">
                                        <input
                                          type="text"
                                          className="form-control form-control-compact me-2 flex-grow-1"
                                          value={opt}
                                          onChange={(e) => handleOptionChange(sIdx, fIdx, optIdx, e.target.value)}
                                          placeholder={`Option ${optIdx + 1}`}
                                        />
                                        <button
                                          type="button"
                                          className="btn btn-small-compact btn-danger"
                                          onClick={() => handleRemoveOption(sIdx, fIdx, optIdx)}
                                        >
                                          <i className="fa-solid fa-trash"></i>
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                </>
                              )}
                              <button
                                type="button"
                                className="btn btn-outline-primary btn-sm me-2 mb-1"
                                onClick={() => handleAddOption(sIdx, fIdx)}
                              >
                                + Add Option
                              </button>
                              <button
                                type="button"
                                className="btn btn-outline-primary btn-sm me-2 mb-1"
                                onClick={() => {
                                  setSelectedFieldForOptions({ sectionIndex: sIdx, fieldIndex: fIdx });
                                  setShowManageModal(true);
                                }}
                              >
                                <i className="fa-solid fa-arrows-up-down"></i> Reorder Options
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="col-md-8">
                          <div className="mt-2">
                            <label className="fw-bold form-label mb-1">Action Rules</label>
                            <ActionRulesInlineBuilder
                              value={field.actionRules || ""}
                              onChange={(newVal) => handleFieldChange(sIdx, fIdx, "actionRules", newVal)}
                              allFields={allFieldsForRules}
                              allSections={allSectionsForRules}
                              forceRawMode={globalRuleCodeMode}
                              disablePreCondition={true}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    <AttachmentUpload
                      sectionIndex={sIdx}
                      fieldIndex={fIdx}
                      field={field}
                      onAttachmentSelect={handleAttachmentFileSelect}
                      onAttachmentDelete={handleAttachmentDelete}
                    />
                  </div>
                ))}

                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => handleAddField(sIdx)}>
                  + Add Question
                </button>
              </div>
            </div>
          ))}

          {/* Bottom Content Spacer */}
          <div style={{ height: "70px" }}></div>

          {/* Floating Action Bar centered in main content area (accounting for sidebar) */}
          <div
            className="floating-action-bar position-fixed bottom-0 mb-3 shadow-lg border rounded-4 bg-white px-3 py-2 d-flex align-items-center justify-content-between"
            style={{
              zIndex: 1040,
              transform: "translateX(-50%)",
            }}
          >
            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => {
                  setShowManageModal(true);
                  setSelectedFieldForOptions(null);
                }}
              >
                <i className="fa-solid fa-arrows-up-down me-1"></i> Manage Sect
              </button>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={handleAddSection}
              >
                <i className="fa-solid fa-plus me-1"></i> Add Section
              </button>
              <button
                type="button"
                className={`btn btn-sm ${globalRuleCodeMode ? 'btn-dark' : 'btn-outline-info'}`}
                onClick={() => setGlobalRuleCodeMode(!globalRuleCodeMode)}
                title="Toggle Action Rules Mode for all fields"
              >
                <i className={`fa-solid ${globalRuleCodeMode ? 'fa-code' : 'fa-border-all'} me-1`}></i>
                Rules: {globalRuleCodeMode ? 'Code Mode' : 'Visual Mode (Beta)'}
              </button>
            </div>

            <div className="d-flex align-items-center gap-2">
              <Link to="/form">
                <button type="button" className="btn btn-light btn-sm text-secondary border px-3">
                  Cancel
                </button>
              </Link>
              <button
                type="button"
                className="btn btn-primary btn-sm px-4 fw-bold"
                onClick={handleSubmit}
                disabled={isLoadingSave || !isDirty}
                title={!isDirty ? "Tidak ada perubahan untuk disimpan" : undefined}
              >
                {isLoadingSave && <LoadingSpinner reverse={true} />} Save
              </button>
            </div>
          </div>

          <style>{`
        .floating-action-bar {
          left: calc(50% + 125px);
          width: calc(100vw - 250px - 4rem);
          max-width: 1350px;
        }
        @media (max-width: 991.98px) {
          .floating-action-bar {
            left: 50% !important;
            width: calc(100vw - 2rem) !important;
          }
        }
      `}</style>
        </div>
      </div>

      {showManageModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {selectedFieldForOptions
                    ? `Manage Options - ${selectedField?.label || 'Field'}`
                    : "Manage Sections & Fields"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowManageModal(false);
                    setSelectedFieldForOptions(null);
                  }}
                ></button>
              </div>

              <div className="modal-body">
                {selectedFieldForOptions && selectedField ? (
                  <div>
                    <div className="mb-3">
                      <h6>Field: <strong>{selectedField.label || `Field ${selectedFieldForOptions.fieldIndex + 1}`}</strong></h6>
                      <p className="text-muted mb-0">
                        Section: {sections[selectedFieldForOptions.sectionIndex]?.name}
                      </p>
                    </div>

                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragOptionEndInModal}
                    >
                      <SortableContext
                        items={selectedField.option?.map((_, optIdx) => `modal-option-${optIdx}`) || []}
                        strategy={verticalListSortingStrategy}
                      >
                        <div className="mb-3">
                          {selectedField.option && selectedField.option.length > 0 ? (
                            selectedField.option.map((opt, optIdx) => (
                              <SortableOptionItemModal
                                key={`modal-option-${optIdx}`}
                                id={`modal-option-${optIdx}`}
                                optionIndex={optIdx}
                                value={opt}
                                onRemove={handleRemoveOptionInModal}
                                onChange={handleOptionChangeInModal}
                              />
                            ))
                          ) : (
                            <div className="alert alert-warning">
                              <i className="fa-solid fa-exclamation-triangle me-2"></i>
                              No options available. Add some options first.
                            </div>
                          )}
                        </div>
                      </SortableContext>
                    </DndContext>

                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={handleAddOptionInModal}
                      >
                        + Add Option
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => setSelectedFieldForOptions(null)}
                      >
                        <i className="fa-solid fa-arrow-left me-1"></i> Back to Sections
                      </button>
                    </div>
                  </div>
                ) : (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(event) => {
                      const { active, over } = event;
                      if (!over || active.id === over.id) return;

                      const activeId = String(active.id);
                      const overId = String(over.id);

                      if (activeId.startsWith("section-") && overId.startsWith("section-")) {
                        const oldIndex = parseInt(activeId.split("-")[1]);
                        const newIndex = parseInt(overId.split("-")[1]);
                        setSections((prev) => arrayMove(prev, oldIndex, newIndex));
                        return;
                      }

                      if (activeId.startsWith("field-") && overId.startsWith("field-")) {
                        const [_, fromSection, fromField] = activeId.split("-").map(Number);
                        const [__, toSection, toField] = overId.split("-").map(Number);
                        setSections((prev) => {
                          const updated = [...prev];
                          const [moved] = updated[fromSection].fields.splice(fromField, 1);
                          updated[toSection].fields.splice(toField, 0, moved);
                          return updated;
                        });
                      }
                    }}
                  >
                    <SortableContext
                      items={sections.map((_, idx) => `section-${idx}`)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="accordion" id="sectionAccordion">
                        {sections.map((section, sIdx) => {
                          const isOpen = openSectionsInModal[sIdx] !== undefined ? openSectionsInModal[sIdx] : false;
                          return (
                            <SortableSectionItem
                              key={`section-${sIdx}`}
                              id={`section-${sIdx}`}
                              section={section}
                              index={sIdx}
                              isOpen={isOpen}
                              onToggle={() => toggleSectionInModal(sIdx)}
                              onDelete={(index) => {
                                if (window.confirm(`Delete section "${sections[index].name}"?`))
                                  handleRemoveSection(index);
                              }}
                            >
                              <SortableContext
                                items={section.fields.map((_, fIdx) => `field-${sIdx}-${fIdx}`)}
                                strategy={verticalListSortingStrategy}
                              >
                                {section.fields.length === 0 ? (
                                  <p className="text-muted fst-italic">No fields in this section.</p>
                                ) : (
                                  <ul className="list-group">
                                    {section.fields.map((f, fIdx) => (
                                      <SortableFieldItem
                                        key={`field-${sIdx}-${fIdx}`}
                                        id={`field-${sIdx}-${fIdx}`}
                                        sectionIndex={sIdx}
                                        fieldIndex={fIdx}
                                        field={f}
                                        onDelete={(s, f) => {
                                          if (window.confirm("Are you sure you want to delete this field?"))
                                            handleRemoveField(s, f);
                                        }}
                                      />
                                    ))}
                                  </ul>
                                )}
                              </SortableContext>
                            </SortableSectionItem>
                          );
                        })}
                      </div>
                    </SortableContext>
                  </DndContext>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setShowManageModal(false);
                    setSelectedFieldForOptions(null);
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FormFactory;
